/* eslint-disable no-underscore-dangle */
import React, { useContext } from 'react';
import {
  Eye, History, Move, Pencil, Trash, Users, File,
  Bookmark,
} from 'lucide-react';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import PermissionLabel from 'components/PermissionsPopup/PermissionLabel';
import { ButtonDotsMenu, Skeleton } from 'xland-ui';
import { initPermission } from 'utils/functions/initPermissions';
import { PERMISSION_SCOPES } from 'constants/permissions';
import { TOOL_DATA_ROOM } from 'constants/tools';
import { selectType } from '../utils';
import { DocumentsContext } from '../provider/DocumentsProvider';

function ContentsList() {
  const {
    literals,
    literalsCommon,
    startup,
    actualFolder,
    content,

    setShowInfoPopup,
    setShowViewersPopup,
    setShowEditPopup,
    setShowVersionsPopup,
    setShowMovePopup,
    handleBookmark,
    handleDelete,

    loading,
    canEdit,
  } = useContext(DocumentsContext);

  const getRowActions = (type, item) => {
    const permission = initPermission(item, PERMISSION_SCOPES.STARTUP, startup.id, TOOL_DATA_ROOM);

    return [
      {
        icon: Eye, text: literals.details, onClick: () => setShowInfoPopup(item),
      },
      {
        icon: Users, text: literals.views, onClick: () => setShowViewersPopup(item), hidden: type !== 'document',
      },
      {
        icon: Pencil, text: literalsCommon.edit, onClick: () => setShowEditPopup(item), hidden: !permission.canEdit,
      },
      {
        icon: History, text: literals.versions, onClick: () => setShowVersionsPopup(item), hidden: !permission.canEdit || type !== 'document',
      },
      {
        icon: Move, text: literals.move, onClick: () => setShowMovePopup(item), hidden: !permission.canEdit,
      },
      {
        icon: Trash, mode: 'danger', text: literalsCommon.delete, onClick: () => handleDelete(item), hidden: !permission.canEdit,
      },
    ];
  };

  const getBookmarkFill = (item) => {
    if (item._bookmark === 'loading') {
      return 'var(--content-low)';
    }
    if (item._bookmark) {
      return 'var(--content-high)';
    }
    return 'var(--bg-none)';
  };

  const renderDraggableRow = (type, item, index) => {
    const rowContent = (
      <div className='d-flex justify-content-between align-items-center h-100'>
        <div className='content-left'>
          {selectType(item?.type || item?._type, 16)}
          <div className='content-info'>
            <span>{item.name}</span>
            <PermissionLabel permission={item.permission} />
          </div>
        </div>
        <div className='dots-menu'>
          <Bookmark
            size={18}
            fill={getBookmarkFill(item)}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleBookmark(item);
            }}
          />
          <ButtonDotsMenu
            size={20}
            buttons={getRowActions(type, item)}
          />
        </div>
      </div>
    );
    return (
      <Draggable
        index={index}
        draggableId={`content-${item.id}`}
        isDragDisabled={!canEdit || ['recent', 'bookmark'].includes(actualFolder?.id)}
      >
        {
          (draggableProvider, draggableSnapshot) => (
            <>
              <li
                ref={draggableProvider.innerRef}
                {...draggableProvider.draggableProps}
                {...draggableProvider.dragHandleProps}
                className={`li-container ${loading ? 'disabled' : ''}`}
                style={{
                  ...draggableProvider.draggableProps.style,
                  transform: draggableSnapshot.isDragging
                    ? draggableProvider.draggableProps.style.transform : '',
                }}
              >
                {rowContent}
              </li>
              {draggableSnapshot.isDragging && (
                <li className='li-container folder-placeholder item'>
                  {rowContent}
                </li>
              )}
            </>
          )
        }
      </Draggable>
    );
  };

  if (loading) {
    const auxFiles = actualFolder?.stats
      ? actualFolder.stats.documents + actualFolder.stats.links : 6;

    return auxFiles ? (
      <ul className='folder-folders'>
        {Array.from(new Array(auxFiles)).map((_, index) => (
          <li key={index} className='li-container d-flex align-items-center h-100'>
            <File size={16} className='me-3' />
            <Skeleton key={index} width='70%' height='10px' />
          </li>
        ))}
      </ul>
    ) : null;
  }

  return content?.items?.length > 0 && (
    <ul className='folder-folders'>
      {content.items.map((cont, i) => (
        <Droppable key={cont.id} droppableId={`droppable-${cont.id}`} isDropDisabled>
          {(provider) => (
            <React.Fragment ref={provider.innerRef}>
              {renderDraggableRow(cont?._type, cont, i)}
              <div className='folder-dropzone'>
                <span className='fc-secondary'>{literals.dropInside}</span>
                {provider.placeholder}
              </div>
            </React.Fragment>
          )}
        </Droppable>
      ))}
    </ul>
  );
}

export default ContentsList;
